import { useCallback, useEffect, useMemo, useRef } from 'react';

import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core-react/router';

import { hasSearchParams, parseUrlToState, stateToUrlSearch } from '#src/app/pages/searchV2/shared/api';
import type { SearchActions, SearchState } from '#src/app/pages/searchV2/shared/api';

type ApplyOptions = {
	// When the resulting URL would be empty, keep the results view active by navigating to `?q=`
	// instead of the bare path (which would show the landing page). Reset opts out of this.
	keepActive?: boolean;
};

/**
 * URL-mutating search actions. Callbacks are stable: they read the latest state from a ref at call
 * time and navigate to the derived URL, so the URL stays the single source of truth.
 */
export function useSearchActions(): SearchActions {
	const location = useLocation();
	const navigate = useNavigate();

	// Keep the latest location in a ref so the action callbacks can stay stable. Actions only ever
	// run from event handlers (after commit), so reading the committed ref is always current.
	const locationRef = useRef(location);
	useEffect(() => {
		locationRef.current = location;
	}, [location]);

	const apply = useCallback(
		(updater: (state: SearchState) => SearchState, { keepActive = true }: ApplyOptions = {}) => {
			const current = parseUrlToState(locationRef.current.search);
			const next = updater(current);
			const newSearch = stateToUrlSearch(next) || (keepActive ? '?q=' : '');
			navigate(`${locationRef.current.pathname}${newSearch}`, { replace: !hasSearchParams(next) });
		},
		[navigate],
	);

	return useMemo(
		(): SearchActions => ({
			setQuery: (query) => apply((s) => ({ ...s, query, page: 0 })),
			clearQuery: () => apply((s) => ({ ...s, query: '', page: 0 })),
			setPage: (page) => apply((s) => ({ ...s, page })),
			toggleTopic: (topic) =>
				apply((s) => ({
					...s,
					topics: s.topics.includes(topic) ? s.topics.filter((t) => t !== topic) : [...s.topics, topic],
					page: 0,
				})),
			clearTopics: () => apply((s) => ({ ...s, topics: [], page: 0 })),
			setStatus: (status) => apply((s) => ({ ...s, status, page: 0 })),
			setLocationCity: (city) => apply((s) => ({ ...s, locationCities: [city], locationCountries: [], page: 0 })),
			setLocationCountry: (country) =>
				apply((s) => ({ ...s, locationCountries: [country], locationCities: [], page: 0 })),
			clearLocation: () => apply((s) => ({ ...s, locationCities: [], locationCountries: [], page: 0 })),
			setSortBy: (sortBy) => apply((s) => ({ ...s, sortBy, page: 0 })),
			clearAllFilters: () =>
				apply((s) => ({
					...s,
					topics: [],
					status: 'all',
					locationCities: [],
					locationCountries: [],
					page: 0,
				})),
			resetState: () => apply(() => parseUrlToState(''), { keepActive: false }),
		}),
		[apply],
	);
}
